import { create } from 'zustand';
import { EMPTY_FILTERS } from './boardFilters';
import { loadLastOpened, saveLastOpened, withOpened, loadOverviewPrefs, saveOverviewPrefs } from './boardsLocalPrefs';
import { notifyMcpSurfaceChanged, ORIGIN_APP } from '../../../mcp/notify';

/**
 * Tiny cross-component nonce so the Task Manager tab reloads its board list /
 * open board when records change out of band — e.g. the personal sync engine
 * applying a change pulled from another device. Mirrors documentsUiStore.
 *
 * It also holds two pieces of purely local board UI state:
 *
 * - `teamMembersByTeam` — the assignee picker's member list, fetched ONCE per
 *   board mount. A 40-card board must not make 40 requests, so the cache is
 *   keyed by team and shared by every card and the detail modal.
 * - `filters` — the filter bar. Deliberately in a plain (unpersisted) store:
 *   filters must not survive a reload, because a board hiding most of its
 *   cards for a reason the user can no longer see is indistinguishable from a
 *   broken board. They must equally never enter a board's `data` or reach
 *   sync — nothing here is ever written back into a task.
 */
export const useBoardsUiStore = create((set) => ({
  boardsVersion: 0,
  bumpBoards: () => set((s) => ({ boardsVersion: s.boardsVersion + 1 })),

  // Boards overview (cards 7.2/7.3). `lastOpened` ({ boardId: epoch ms }) and
  // the sort / card-row view are per device in localStorage (boardsLocalPrefs)
  // and never synced; the two quick chips are in memory like `filters`, for
  // the same reason (a grid hiding boards must not survive a reload).
  lastOpened: loadLastOpened(),
  markBoardOpened: (boardId, at = Date.now()) =>
    set((s) => {
      const lastOpened = withOpened(s.lastOpened, boardId, at);
      saveLastOpened(lastOpened);
      return { lastOpened };
    }),
  overviewPrefs: loadOverviewPrefs(),
  setOverviewPrefs: (patch) =>
    set((s) => {
      const overviewPrefs = { ...s.overviewPrefs, ...patch };
      saveOverviewPrefs(overviewPrefs);
      return { overviewPrefs };
    }),
  // Pinned boards, { boardId: pinnedAt ms }. The source of truth is the
  // personal-scope `board_pin` records (boardPins.js, synced per user); this
  // is their in-memory copy, refreshed by loadBoardPins() whenever the board
  // list reloads. Never written into a board record.
  pinned: {},
  setPinned: (pinned) => set(() => ({ pinned: pinned || {} })),
  overviewQuick: { overdue: false, hideCompleted: false },
  setOverviewQuick: (patch) => set((s) => ({ overviewQuick: { ...s.overviewQuick, ...patch } })),

  teamMembersByTeam: {},
  setTeamMembers: (teamId, members) =>
    set((s) => ({
      teamMembersByTeam: { ...s.teamMembersByTeam, [String(teamId)]: members || [] },
    })),
  // teamId -> { boardId: userId } — who created / shared in each team board,
  // as GET /teams/:id/boards says (`created_by`). Read once per team, only for
  // boards made before 1.0.7 that carry no `createdBy` (teamBoardOwners.js).
  teamBoardOwners: {},
  setTeamBoardOwners: (teamId, owners) =>
    set((s) => ({
      teamBoardOwners: { ...s.teamBoardOwners, [String(teamId)]: owners || {} },
    })),

  // A task a chat share card asked to open, as { boardId, taskId }. The board
  // tab is opened first and mounts asynchronously, so the request has to be
  // parked somewhere it can collect it — it cannot be passed as a prop to a
  // component that does not exist yet.
  pendingTaskFocus: null,
  requestTaskFocus: (boardId, taskId) =>
    set(() => ({ pendingTaskFocus: { boardId: String(boardId), taskId: String(taskId) } })),
  clearTaskFocus: () => set(() => ({ pendingTaskFocus: null })),

  filters: { ...EMPTY_FILTERS },
  setFilters: (patch) => set((s) => ({ filters: { ...s.filters, ...patch } })),
  resetFilters: () => set(() => ({ filters: { ...EMPTY_FILTERS } })),
}));

/**
 * The boards grid moved. Mirrors `bumpDocumentsTree` exactly, including the
 * `origin` argument and why it exists — see that function.
 */
export function bumpBoards(origin = ORIGIN_APP) {
  useBoardsUiStore.getState().bumpBoards();
  notifyMcpSurfaceChanged('boards', origin);
}

/**
 * Open a board tab and, once it has mounted, its task detail modal. Used by
 * the chat share card — the caller opens the tab, this parks which task the
 * board should surface when it gets there.
 */
export function requestTaskFocus(boardId, taskId) {
  useBoardsUiStore.getState().requestTaskFocus(boardId, taskId);
}

/** Members of one team, or [] when they haven't been fetched yet. */
export function getCachedTeamMembers(teamId) {
  if (teamId == null) return [];
  return useBoardsUiStore.getState().teamMembersByTeam[String(teamId)] || [];
}

/**
 * WS-first fast path for team boards: a plain listener set (mirrors
 * teamChannelService's event handlers), separate from the zustand nonce
 * above, so a live `sync_delta` record batch reaches the open TaskBoardTab
 * instance directly instead of waiting for the engine write + bumpBoards +
 * disk re-read round trip. TaskBoardTab decides whether/how to apply it
 * (open board, not dirty, not mid-drag — see patchBoardWithRecords callers).
 */
const boardChangeListeners = new Set();

export function subscribeBoardChanges(fn) {
  boardChangeListeners.add(fn);
  return () => boardChangeListeners.delete(fn);
}

export function publishBoardChanges(scopeKey, records) {
  boardChangeListeners.forEach((fn) => {
    try {
      fn(scopeKey, records);
    } catch (err) {
      console.error('Board change listener error:', err);
    }
  });
}
